import { useRef, useState } from 'react';

import NextImage, { ImageLoaderProps } from 'next/image';
import InfiniteScroll from 'react-infinite-scroll-component';
import get from 'ts-get';

import { useRutilusQuery } from '../../../../api/graphql/useRutilusQuery';
import { CardButton } from '../../../../components/Cards/CardButton';
import { AppStoreButtons } from '../../../../components/Clickables/Buttons/AppStoreButtons/AppStoreButtons';
import { Heading } from '../../../../components/Heading/Heading';
import { FishCatchesIcon } from '../../../../components/Icons/FishCatchesIcon';
import { LoadingIndicator } from '../../../../components/LoadingIndicator/LoadingIndicator';
import { MAIN_NAVIGATION_MIN_HEIGHT } from '../../../../components/Navigation/MainNavigation/constants/constants';
import { getCatchCardEventProperties } from '../../../../components/PostsViewer/helpers/getCatchCardEventProperties';
import { PostsViewer } from '../../../../components/PostsViewer/PostsViewer';
import { openPostViewerEventSources } from '../../../../components/PostsViewer/types/openPostViewerEventSources';
import { QRCode } from '../../../../components/QRCode/QRCode';
import { Text } from '../../../../components/Text/Text';
import { useAnalytics } from '../../../../contexts/AnalyticsContext/AnalyticsContext';
import { reportMykissError } from '../../../../helpers/errorHandling';
import { getNodesFromConnection } from '../../../../helpers/graphql';
import { usePostViewer } from '../../../../helpers/hooks/usePostViewer';
import { useUserInfo } from '../../../../helpers/hooks/useUserInfo';
import { GetUsersCatches, GetUsersCatchesVariables } from '../../../../interfaces/graphql';
import { getUsersCatches } from '../../../../queries/rutilus/Users';
import signupBackground from '../../assets/signupBackground.svg';
import { AnglerCatchesViewLoader } from './AnglerCatchesViewLoader';
import { getImgProxyLoader } from '../../../../helpers/imageLoaders/imgProxy';

interface IProps {
  nickname: string;
  usersOwnPage: boolean;
  totalAnglerCatches: number;
  className?: string;
}
const FREE_USER_CATCH_LIMIT = 6;
const USER_CATCH_LIMIT = 14;

export const AnglerCatchesView = ({
  nickname,
  usersOwnPage,
  totalAnglerCatches,
  className,
}: IProps) => {
  const { currentPage } = useAnalytics();
  const { isLoggedIn } = useUserInfo();
  const first = isLoggedIn ? USER_CATCH_LIMIT : FREE_USER_CATCH_LIMIT;
  const variables: GetUsersCatchesVariables = { nickname, first };
  const postsReference = useRef<HTMLDivElement>(null);
  const {
    currentPostViewerId,
    closePostViewer,
    isPostViewerOpen,
    openPostViewer,
    setCurrentPostViewerId,
  } = usePostViewer(postsReference, MAIN_NAVIGATION_MIN_HEIGHT + 4);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const { data, error, loading, fetchMore } = useRutilusQuery<
    GetUsersCatches,
    GetUsersCatchesVariables
  >(getUsersCatches, {
    variables,
  });

  if (error) {
    return <></>;
  }

  if (!data || loading) {
    return <AnglerCatchesViewLoader />;
  }

  const angler = data.user;
  const { hasNextPage, endCursor } = angler.catches.pageInfo;
  const anglerCatchPosts = getNodesFromConnection(angler.catches).map(_catch => _catch.post);
  const imgAria = `A catch by ${nickname}`;
  const emptyCatchText = `It looks like ${nickname} hasn't logged any catches yet`;

  const fetchNextPage = async (): Promise<unknown> => {
    setIsFetchingMore(true);
    return fetchMore({
      variables: {
        ...variables,
        after: endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }): GetUsersCatches => {
        if (!fetchMoreResult.user.catches) {
          return prev;
        }
        return {
          user: {
            ...fetchMoreResult.user,
            catches: {
              ...fetchMoreResult.user.catches,
              edges: [
                ...get(prev, p => p.user.catches.edges, []),
                ...get(fetchMoreResult, r => r.user.catches.edges, []),
              ],
            },
          },
        };
      },
    })
      .then(() => {
        setIsFetchingMore(false);
      })
      .catch(reportMykissError);
  };

  return (
    <div className={className} data-testid="AnglerCatchesView">
      <InfiniteScroll
        next={fetchNextPage}
        hasMore={isLoggedIn ? hasNextPage : false}
        loader={<></>}
        dataLength={anglerCatchPosts.length}
        className="mb-8 grid gap-4 sm:grid-cols-2"
      >
        {anglerCatchPosts.map(post => {
          const image = getNodesFromConnection(post.images)[0];
          const imageUrl = image.url || '';
          const imageWidth = image.width || 0;
          const imageHeight = image.height;

          const imgProxyLoader = ({ src, width, quality }: ImageLoaderProps): string =>
            getImgProxyLoader(src, width, imageWidth, imageHeight, { quality });

          return (
            <CardButton
              className="relative aspect-3/2 w-full cursor-pointer rounded-md p-0!"
              id={post._id}
              key={post._id}
              eventProperties={getCatchCardEventProperties({
                postId: post._id,
                currentPage,
                source: 'species-top-section',
              })}
              onClick={async () =>
                openPostViewer(post._id, openPostViewerEventSources.AnglerProfile)
              }
              ariaLabel={imgAria}
              data-testid="AnglerCatchesView-CardButton"
            >
              <NextImage
                alt="angler catch"
                className="size-full rounded-md object-cover"
                src={imageUrl}
                loader={imgProxyLoader}
                fill
                sizes="(min-width: 1680px) 115px,(min-width: 990px) 8.33vw ,(min-width: 600px) 16.67vw, 50vw"
              />
            </CardButton>
          );
        })}
      </InfiniteScroll>
      {isFetchingMore && <LoadingIndicator />}
      {anglerCatchPosts.length === 0 && (
        <>
          {usersOwnPage ? (
            <div className="text-midnight mb-10 flex flex-col items-center text-center">
              <Text className="w-2/3 italic">Log your first catch in the fishbrain app today!</Text>
              <div className="text-twilight">
                <FishCatchesIcon size={100} />
              </div>
              <AppStoreButtons source="AnglersCatchesView" />
            </div>
          ) : (
            <div className="mb-8 flex flex-col items-center justify-center text-center">
              <Text className="w-5/6 sm:w-1/2">{emptyCatchText}</Text>
              <div className="text-twilight">
                <FishCatchesIcon size={100} />
              </div>
            </div>
          )}
        </>
      )}
      {!isLoggedIn && totalAnglerCatches > FREE_USER_CATCH_LIMIT && (
        <div
          className="bg-lake-fog my-8 flex w-full flex-col items-center gap-4 rounded-lg bg-contain bg-no-repeat p-6"
          style={{
            backgroundImage: `url(${signupBackground.src})`,
          }}
        >
          <Heading variant="sm" as="h3" className="text-center">
            See all catches from {nickname} in the Fishbrain app
          </Heading>
          <Text variant="sm" className="mx-4 mb-4 text-center">
            Download the app to see more info about your favorite anglers, ability to follow them
            and see what they catch next
          </Text>
          <div className="flex gap-4">
            <AppStoreButtons
              orientation="vertical"
              className="justify-center"
              source="AnglersCatchesView"
            />
            <QRCode className="hidden w-full sm:flex" source="AnglersCatchesView" />
          </div>
        </div>
      )}
      {isPostViewerOpen && currentPostViewerId && (
        <PostsViewer
          posts={anglerCatchPosts}
          currentPostViewerId={currentPostViewerId}
          hasMore={isLoggedIn ? hasNextPage : false}
          isPostViewerOpen
          loadMore={fetchNextPage}
          onClosePostsViewer={closePostViewer}
          setCurrentPostViewerId={setCurrentPostViewerId}
        />
      )}
    </div>
  );
};
